@if (tour(); as tour) {
  <div class="page-head">
    <div>
      <p class="eyebrow">รายละเอียดทัวร์บริษัท</p>
      <h1>{{ tour.tourNo }}</h1>
      @if (canViewCommissionDetails()) {
        <p>ข้อมูลทัวร์ Booking สมาชิก ค่าใช้จ่าย และคอมมิชชั่นระดับทัวร์</p>
      } @else {
        <p>ข้อมูลทัวร์ Booking สมาชิก และค่าใช้จ่ายระดับทัวร์</p>
      }
    </div>
    <div class="flex flex-wrap items-center gap-2">
      @if (tour.pdfUrl) {
        <a
          [href]="tour.pdfUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-red-600 bg-red-600 px-4 text-[15px] font-semibold text-white no-underline transition hover:border-red-700 hover:bg-red-700"
          ><lucide-icon name="FileText" [size]="17" />เปิดเอกสาร PDF</a
        >
      }
      <div class="flex flex-col items-end gap-2">
        <a
          routerLink="/tours"
          class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-[15px] font-semibold text-slate-700 no-underline transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
          ><lucide-icon name="ArrowLeft" [size]="17" />กลับหน้ารายการ</a
        >
        <app-refresh-button
          class="detail-floating-refresh fixed top-24 z-20 -translate-x-1/2 transition-opacity duration-200 sm:top-28"
          [class.pointer-events-none]="isRefreshButtonHidden()"
          [class.opacity-0]="isRefreshButtonHidden()"
          label="รีเฟรชรายละเอียดทัวร์"
          [loading]="isRefreshing()"
          [prominent]="true"
          (refresh)="refreshTourDetails()"
        />
      </div>
    </div>
  </div>

  <section
    class="mb-5 grid gap-3 sm:grid-cols-2"
    [class.xl:grid-cols-3]="!canViewCommissionDetails()"
    [class.xl:grid-cols-4]="canViewCommissionDetails()"
  >
    <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div class="flex items-center justify-between gap-3">
        <p class="m-0 text-sm font-semibold text-slate-600">สมาชิกทัวร์</p>
        <span
          class="grid size-9 place-items-center rounded-xl bg-sky-50 text-sky-700 ring-1 ring-sky-200"
          ><lucide-icon name="Users" [size]="18"
        /></span>
      </div>
      <strong class="mt-3 block text-xl text-slate-800"
        >{{ tour.currentPax }} / {{ tour.requiredPax }} คน</strong
      ><small class="mt-1 block text-slate-500">สมาชิกที่เข้าร่วมทัวร์</small>
    </article>
    <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div class="flex items-center justify-between gap-3">
        <p class="m-0 text-sm font-semibold text-slate-600">มูลค่าทัวร์รวม</p>
        <span
          class="grid size-9 place-items-center rounded-xl bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200"
          ><lucide-icon name="CircleDollarSign" [size]="18"
        /></span>
      </div>
      <strong class="mt-3 block text-xl text-slate-800"
        >{{ bookingNetSalesTotal() | number: '1.2-2' }} บาท</strong
      ><small class="mt-1 block text-slate-500">จาก {{ tour.bookingCount }} Booking</small>
    </article>
    <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div class="flex items-center justify-between gap-3">
        <p class="m-0 text-sm font-semibold text-slate-600">ค่าใช้จ่ายจริง</p>
        <span
          class="grid size-9 place-items-center rounded-xl bg-red-50 text-red-700 ring-1 ring-red-200"
          ><lucide-icon name="ReceiptText" [size]="18"
        /></span>
      </div>
      <strong class="mt-3 block text-xl text-slate-800"
        >{{ verifiedExpenseTotal() | number: '1.2-2' }} บาท</strong
      ><small class="mt-1 block text-slate-500"
        >ประมาณการ {{ totalExpense() | number: '1.2-2' }} บาท</small
      >
    </article>
    @if (canViewCommissionDetails()) {
      <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div class="flex items-center justify-between gap-3">
          <p class="m-0 text-sm font-semibold text-slate-600">คอมมิชชั่น</p>
          <span
            class="grid size-9 place-items-center rounded-xl bg-violet-50 text-violet-700 ring-1 ring-violet-200"
            ><lucide-icon name="BadgePercent" [size]="18"
          /></span>
        </div>
        <strong class="mt-3 block text-xl text-slate-800"
          >{{ totalCommission() | number: '1.2-2' }} บาท</strong
        ><small class="mt-1 block text-slate-500"
          >Sale {{ saleCommissionTotal() | number: '1.2-2' }} บาท · ผู้ดูแล
          {{ adminCommissionTotal() | number: '1.2-2' }} บาท</small
        >
      </article>
    }
  </section>

  <div class="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
    <div class="grid min-w-0 gap-5">
      <section class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div
          class="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4"
        >
          <div>
            <h2 class="m-0 text-lg font-bold text-slate-800">ข้อมูลทัวร์</h2>
            <p class="mt-1 mb-0 text-xs text-slate-500">รายละเอียดหลักของทัวร์บริษัท</p>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <app-status-badge [status]="tour.type" />
            <app-status-badge [status]="tour.status" />
          </div>
        </div>
        @if (tour.profileUrl) {
          <img
            [src]="tour.profileUrl"
            [alt]="'ภาพทัวร์ ' + tour.tourName"
            width="400"
            height="400"
            class="mx-auto mb-5 block aspect-square w-full max-w-[400px] rounded-xl border border-slate-200 bg-slate-100 object-cover object-center"
          />
        } @else {
          <div
            class="mx-auto mb-5 grid aspect-square w-full max-w-[400px] place-items-center rounded-xl border border-dashed border-slate-300 bg-slate-100 text-slate-400"
          >
            <div class="text-center">
              <lucide-icon name="Image" [size]="40" />
              <p class="mt-2 mb-0 text-sm">ยังไม่มีภาพทัวร์</p>
            </div>
          </div>
        }
        <dl
          class="grid gap-x-6 sm:grid-cols-2 lg:grid-cols-3 [&>div]:border-b [&>div]:border-slate-100 [&>div]:py-3 [&_dt]:text-xs [&_dt]:text-slate-500 [&_dd]:mt-1 [&_dd]:mb-0 [&_dd]:text-sm [&_dd]:font-semibold [&_dd]:text-slate-800"
        >
          <div>
            <dt>รหัสทัวร์</dt>
            <dd>{{ tour.code }}</dd>
          </div>
          <div class="sm:col-span-2 lg:col-span-2">
            <dt>ชื่อทัวร์</dt>
            <dd>{{ tour.tourName }}</dd>
          </div>
          <div>
            <dt>วันเดินทางไป</dt>
            <dd>{{ tour.travelDate | date: 'dd MMM yyyy' }}</dd>
          </div>
          <div>
            <dt>วันเดินทางกลับ</dt>
            <dd>{{ tour.returnDate ? (tour.returnDate | date: 'dd MMM yyyy') : '—' }}</dd>
          </div>
          <div>
            <dt>สมาชิกทัวร์</dt>
            <dd>{{ tour.currentPax }} / {{ tour.requiredPax }} คน</dd>
          </div>
          <div>
            <dt>จำนวน Booking</dt>
            <dd>{{ tour.bookingCount }} รายการ</dd>
          </div>
          <div>
            <dt>ผู้สร้าง</dt>
            <dd>{{ tour.createdByName }}</dd>
          </div>
          <div>
            <dt>คอม Sale ต่อคน</dt>
            <dd>{{ tour.saleCommission | number: '1.2-2' }} บาท</dd>
          </div>
          <div>
            <dt>คอมผู้ดูแลทัวร์</dt>
            <dd>{{ tour.adminCommissionPercent | number: '1.0-0' }} %</dd>
          </div>
          <div class="sm:col-span-2 lg:col-span-3">
            <dt>รายละเอียดโปรแกรม</dt>
            <dd class="whitespace-pre-line leading-6">{{ tour.programDetail || '—' }}</dd>
          </div>
          <div class="sm:col-span-2 lg:col-span-3">
            <dt>หมายเหตุ</dt>
            <dd class="whitespace-pre-line leading-6">{{ tour.note || '—' }}</dd>
          </div>
        </dl>
      </section>
    </div>

    <aside class="w-full">
      <section class="w-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <h2 class="m-0 text-lg font-bold text-slate-800">สรุปราคาและงบประมาณ</h2>
        <div class="mt-5 grid gap-4 text-sm">
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">จำนวนคนตาม Booking</span
            ><strong class="text-slate-800">{{ tour.currentPax | number }} คน</strong>
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ยอดค่าทัวร์เดิมทั้งหมด</span
            ><strong class="text-slate-800">{{ originalTourTotalAmount() | number: '1.2-2' }} บาท</strong>
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ราคาเพิ่มทั้งหมด</span
            ><strong class="text-violet-700">+{{ approvedTourPriceDelta() | number: '1.2-2' }} บาท</strong>
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="font-semibold text-slate-700">ยอดค่าทัวร์ปัจจุบันทั้งหมด</span
            ><strong class="text-lg text-sky-700">{{ tour.tourTotalAmount | number: '1.2-2' }} บาท</strong>
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ราคาทัวร์ปัจจุบันต่อคน</span
            ><strong class="text-sky-700">{{ tour.tourPrice | number: '1.2-2' }} บาท</strong>
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ส่วนลดทั้งหมด</span>
            <strong class="text-red-600"
              >-{{ totalBookingDiscount() | number: '1.2-2' }} บาท</strong
            >
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ราคาที่เก็บเพิ่มทั้งหมด</span>
            <strong class="text-emerald-700"
              >+{{ approvedCustomerChargeDelta() | number: '1.2-2' }} บาท</strong
            >
          </div>
          <div class="border-t border-slate-200 pt-4">
            <div class="flex items-end justify-between gap-4">
              <span class="font-semibold text-slate-700">ยอดรวมสุทธิทั้งหมด</span
              ><strong class="text-xl text-sky-700"
                >{{ bookingNetSalesTotal() | number: '1.2-2' }} บาท</strong
              >
            </div>
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">รับชำระแล้ว</span>
            <strong class="text-emerald-700"
              >{{ verifiedBookingPaymentTotal() | number: '1.2-2' }} บาท</strong
            >
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">หัก ณ ที่จ่าย</span>
            <strong class="text-red-600"
              >-{{ verifiedBookingWithholdingTaxTotal() | number: '1.2-2' }} บาท</strong
            >
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ยอดค้างชำระ</span>
            <strong class="text-amber-700"
              >{{ bookingOutstandingTotal() | number: '1.2-2' }} บาท</strong
            >
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ใช้จ่ายแล้ว</span>
            <strong class="text-red-600">{{ verifiedExpenseTotal() | number: '1.2-2' }} บาท</strong>
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">คืนเงิน</span>
            <strong class="text-red-600">{{ paidRefundTotal() | number: '1.2-2' }} บาท</strong>
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ค่าธรรมเนียม (ลูกค้า)</span>
            <strong class="text-slate-800"
              >{{ bookingCustomerCreditCardFeeTotal() | number: '1.2-2' }} บาท</strong
            >
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ค่าธรรมเนียม (บริษัท)</span>
            <strong class="text-red-600"
              >{{ bookingCompanyCreditCardFeeTotal() | number: '1.2-2' }} บาท</strong
            >
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ยอดคงเหลือ</span>
            <strong class="text-amber-700"
              >{{ bookingAvailableBalanceTotal() | number: '1.2-2' }} บาท</strong
            >
          </div>
          <div class="border-t border-slate-200 pt-4">
            <div class="flex items-center justify-between gap-4">
              <span class="text-slate-500">ค่าใช้จ่ายประมาณการเดิม</span>
              <strong class="text-slate-800">{{ originalEstimatedExpense() | number: '1.2-2' }} บาท</strong>
            </div>
            <div class="mt-3 flex items-center justify-between gap-4">
              <span class="text-slate-500">ค่าใช้จ่ายที่เพิ่ม</span>
              <strong class="text-violet-700">+{{ approvedExpenseBudgetDelta() | number: '1.2-2' }} บาท</strong>
            </div>
            <div class="mt-3 flex items-center justify-between gap-4">
              <span class="text-slate-500">ที่บริษัทสำรองจ่าย</span>
              <strong class="text-sky-700">+{{ approvedCompanyAdvanceAmount() | number: '1.2-2' }} บาท</strong>
            </div>
            <div class="mt-3 flex items-center justify-between gap-4">
              <span class="font-semibold text-slate-700">ค่าใช้จ่ายประมาณการปัจจุบัน</span>
              <strong class="text-red-600">{{ tour.estimatedExpense | number: '1.2-2' }} บาท</strong>
            </div>
            <div class="mt-3 flex items-center justify-between gap-4">
              <span class="text-slate-500">ค่าใช้จ่ายที่จ่ายไป</span>
              <strong class="text-emerald-700">{{ verifiedExpenseTotal() | number: '1.2-2' }} บาท</strong>
            </div>
            @if (canViewCommissionDetails()) {
              <div class="mt-3 flex items-center justify-between gap-4">
                <span class="text-slate-500">ค่าคอมมิชชั่นฝ่ายขาย</span>
                <strong class="text-red-600">
                  -{{ saleCommissionTotal() | number: '1.2-2' }} บาท
                </strong>
              </div>
              <div class="mt-3 flex items-center justify-between gap-4">
                <span class="text-slate-500">ค่าคอมมิชชั่นผู้ดูแล</span>
                <strong class="text-red-600">
                  -{{ adminCommissionTotal() | number: '1.2-2' }} บาท
                </strong>
              </div>
              <div class="mt-4 flex items-center justify-between gap-4 border-t border-slate-200 pt-4">
                <span class="font-semibold text-slate-700">กำไร</span>
                <strong
                  [class.text-emerald-700]="estimatedProfit() >= 0"
                  [class.text-red-600]="estimatedProfit() < 0"
                  >{{ estimatedProfit() | number: '1.2-2' }} บาท</strong
                >
              </div>
            }
          </div>
          @if (canShowCloseTourCase()) {
            <div class="border-t border-slate-200 pt-4">
              <button
                type="button"
                class="tw-button inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                [disabled]="!canCloseTourCase() || isClosingCase()"
                [title]="canCloseTourCase() ? 'ปิดเคสและสร้างค่าคอมมิชชั่นร่าง' : 'เฉพาะผู้สร้าง ผู้ดูแลหลัก Role 2 การตลาด หรือ Role 1 เท่านั้น'
                "
                (click)="closeTourCase()"
              >
                <lucide-icon name="CircleCheck" [size]="17" aria-hidden="true" />
                ปิดเคส
              </button>
            </div>
          }
        </div>
      </section>
    </aside>
  </div>

  <section class="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div
      class="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-5 sm:p-6"
    >
      <div>
        <h2 class="m-0 text-lg font-bold text-slate-800">ผู้ดูแลทัวร์</h2>
        <p class="mt-1 mb-0 text-xs text-slate-500">ผู้รับผิดชอบการจัดการทัวร์</p>
      </div>
      @if (canAddTourAdministrator()) {
        <button
          type="button"
          class="tw-button inline-flex h-11 items-center justify-center gap-2 self-start rounded-xl bg-[var(--theme-color)] px-4 text-[15px] font-semibold text-white transition hover:bg-[var(--theme-hover)] disabled:cursor-not-allowed disabled:opacity-50 sm:self-auto"
          (click)="openAddAdministrator()"
        >
          <lucide-icon name="Plus" [size]="17" aria-hidden="true" />เพิ่มผู้ดูแล
        </button>
      }
    </div>
    <div class="overflow-x-auto">
      <table class="w-full min-w-[620px] text-sm">
        <thead class="bg-slate-50 text-left text-xs text-slate-500">
          <tr>
            <th class="px-5 py-3 font-semibold">ชื่อผู้ดูแล</th>
            <th class="px-5 py-3 font-semibold">หน้าที่</th>
            <th class="px-5 py-3 text-right font-semibold">ประเภท</th>
            @if (canManagePrimaryTourData()) {
              <th class="w-20 px-5 py-3 text-center font-semibold">จัดการ</th>
            }
          </tr>
        </thead>
        <tbody>
          @for (administrator of tour.administrators; track administrator.id) {
            <tr class="border-t border-slate-100">
              <td class="px-5 py-3 font-semibold text-slate-800">{{ administrator.name }}</td>
              <td class="px-5 py-3 text-slate-700">{{ administrator.responsibility }}</td>
              <td class="px-5 py-3 text-right">
                @if (administrator.isPrimary) {
                  <span
                    class="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700"
                    >ผู้ดูแลหลัก</span
                  >
                } @else {
                  <span
                    class="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600"
                    >ผู้ดูแลร่วม</span
                  >
                }
              </td>
              @if (canManagePrimaryTourData()) {
                <td class="px-5 py-3 text-center">
                  <button
                    type="button"
                    title="ลบผู้ดูแล"
                    aria-label="ลบผู้ดูแล"
                    class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-red-200 bg-white text-red-600 transition hover:bg-red-50"
                    (click)="deleteAdministrator(administrator)"
                  >
                    <lucide-icon name="Trash2" [size]="15" aria-hidden="true" />
                  </button>
                </td>
              }
            </tr>
          } @empty {
            <tr>
              <td
                [attr.colspan]="canManagePrimaryTourData() ? 4 : 3"
                class="px-5 py-10 text-center text-slate-500"
              >
                ยังไม่ระบุผู้ดูแลทัวร์
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>

  <section class="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div class="border-b border-slate-200 p-5 sm:p-6">
      <h2 class="m-0 text-lg font-bold text-slate-800">Booking ที่ร่วมทัวร์</h2>
      <p class="mt-1 mb-0 text-xs text-slate-500">
        แสดง {{ visibleBookings().length }} Booking ที่คุณมีสิทธิ์ดู
      </p>
    </div>
    <div class="overflow-x-auto">
      <table class="w-full min-w-[1040px] border-collapse text-left text-[13px]">
        <thead
          class="bg-slate-50 text-xs font-semibold tracking-wide whitespace-nowrap text-slate-500"
        >
          <tr>
            <th class="w-14 px-4 py-3 text-center">ลำดับ</th>
            <th class="w-44 px-4 py-3">เลขที่ Booking</th>
            <th class="min-w-64 px-4 py-3">ลูกค้า</th>
            <th class="w-32 px-4 py-3">ประเภท</th>
            <th class="w-24 px-4 py-3 text-center">จำนวนคน</th>
            <th class="w-40 px-4 py-3 text-right">ยอดขาย</th>
            <th class="w-40 px-4 py-3 text-right">ชำระแล้ว</th>
            <th class="w-36 px-4 py-3 text-center">สถานะ</th>
            <th class="w-32 px-4 py-3 text-right">จัดการ</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100">
          @for (booking of visibleBookings(); track booking.id; let index = $index) {
            <tr class="transition even:bg-slate-50/40 hover:bg-sky-50/60">
              <td class="px-4 py-4 text-center text-slate-400 tabular-nums align-top">
                {{ index + 1 }}
              </td>
              <td class="px-4 py-4 align-top">
                <span class="font-semibold whitespace-nowrap text-sky-700">
                  {{ booking.bookingNo }}
                </span>
              </td>
              <td class="px-4 py-4 align-top font-medium text-slate-800">
                {{ booking.customerName }}
              </td>
              <td class="px-4 py-4 align-top">
                <app-status-badge [status]="booking.bookingType" />
              </td>
              <td class="px-4 py-4 text-center align-top tabular-nums text-slate-700">
                {{ booking.pax }} คน
              </td>
              <td
                class="px-4 py-4 text-right align-top font-semibold whitespace-nowrap text-slate-800 tabular-nums"
              >
                {{ booking.totalAmount | number: '1.2-2' }} บาท
              </td>
              <td
                class="px-4 py-4 text-right align-top font-semibold whitespace-nowrap text-emerald-700 tabular-nums"
              >
                {{ booking.paidAmount | number: '1.2-2' }} บาท
              </td>
              <td class="px-4 py-4 text-center align-top">
                <div class="grid justify-center gap-2">
                  <div class="grid grid-cols-[4.5rem_1fr] items-center gap-2 whitespace-nowrap">
                    <span class="text-right text-xs text-slate-500">Booking</span>
                    <app-status-badge [status]="booking.status" />
                  </div>
                  <div class="grid grid-cols-[4.5rem_1fr] items-center gap-2 whitespace-nowrap">
                    <span class="text-right text-xs text-slate-500">ชำระเงิน</span>
                    <app-status-badge [status]="booking.paymentStatus" />
                  </div>
                </div>
              </td>
              <td class="px-4 py-4 text-right align-top">
                <div class="flex items-center justify-end gap-1.5">
                  <a
                    [routerLink]="['/bookings', booking.id]"
                    [queryParams]="{ from: tour.type === 'GROUP_TOUR' ? 'group-bookings' : 'tour', tourId: tour.id }"
                    title="ดูรายละเอียด Booking"
                    aria-label="ดูรายละเอียด Booking"
                    class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 no-underline transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
                  >
                    <lucide-icon name="Eye" [size]="15" />
                  </a>
                  @if (canModifyBooking(booking)) {
                    <button
                      type="button"
                      class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                      title="แก้ไข Booking"
                      aria-label="แก้ไข Booking"
                      [disabled]="isBookingDeleted(booking) || isDeletingBooking()"
                      (click)="openEditBookingModal(booking)"
                    >
                      <lucide-icon name="Pencil" [size]="15" />
                    </button>
                    <button
                      type="button"
                      class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-red-200 bg-white text-red-600 hover:bg-red-50 disabled:opacity-40"
                      title="ลบ Booking"
                      aria-label="ลบ Booking"
                      [disabled]="isBookingDeleted(booking) || isDeletingBooking()"
                      (click)="deleteBooking(booking)"
                    >
                      <lucide-icon name="Trash2" [size]="15" />
                    </button>
                  }
                </div>
              </td>
            </tr>
          } @empty {
            <tr>
              <td colspan="9" class="px-4 py-10 text-center text-slate-500">
                ไม่มีรายการ Booking ที่คุณมีสิทธิ์ดูในทัวร์นี้
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>

  <section class="mt-5 rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div
      class="flex flex-col gap-3 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
    >
      <div>
        <h2 class="m-0 text-lg font-bold text-slate-800">เบิกจ่าย</h2>
        <p class="mt-1 mb-0 text-sm text-slate-500">รายการขอเบิกค่าใช้จ่ายของทัวร์นี้</p>
      </div>
      @if (canManageTourExpenses()) {
        <button
          type="button"
          class="tw-button inline-flex h-11 items-center justify-center gap-2 self-start rounded-xl bg-[var(--theme-color)] px-4 text-[15px] font-semibold text-white transition hover:bg-[var(--theme-hover)] disabled:cursor-not-allowed disabled:opacity-50 sm:self-auto"
          [disabled]="!canCreateExpense()"
          [title]="
            canCreateExpense() ? 'เบิกค่าใช้จ่าย' : 'ยังไม่สามารถเบิกค่าใช้จ่ายได้ตามเงื่อนไขของทัวร์'
          "
          (click)="openExpenseModal()"
        >
          <lucide-icon name="FileText" [size]="17" aria-hidden="true" />เบิกค่าใช้จ่าย
        </button>
      }
    </div>
    <div class="overflow-x-auto">
      <table class="w-full min-w-[980px] border-collapse text-left text-[13px]">
        <thead
          class="bg-slate-50 text-xs font-semibold tracking-wide whitespace-nowrap text-slate-500"
        >
          <tr>
            <th class="w-14 px-4 py-3 text-center">ลำดับ</th>
            <th class="w-52 px-4 py-3">รายการเบิก</th>
            <th class="min-w-64 px-4 py-3">รายละเอียด</th>
            <th class="w-48 px-4 py-3">เอกสาร</th>
            <th class="w-44 px-4 py-3 text-right">จำนวนเงิน</th>
            <th class="w-32 px-4 py-3 text-center">สถานะ</th>
            @if (canManageTourExpenses()) {
              <th class="w-28 px-4 py-3 text-right">ดำเนินการ</th>
            }
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100">
          @for (expense of tour.expenses; track expense.id; let index = $index) {
            <tr class="transition even:bg-slate-50/40 hover:bg-sky-50/60">
              <td class="px-4 py-4 text-center text-slate-400 tabular-nums align-top">
                {{ index + 1 }}
              </td>
              <td class="px-4 py-4 align-top">
                <p class="m-0 font-semibold whitespace-nowrap text-sky-700">
                  {{ expense.expenseNo }}
                </p>
                <p class="mt-1 mb-0 text-xs text-slate-600">{{ expense.name }}</p>
              </td>
              <td class="px-4 py-4 align-top">
                <p class="m-0 leading-5 text-slate-700">{{ expense.description || '—' }}</p>
                <p class="mt-1.5 mb-0 text-xs text-slate-500">ผู้ขอ: {{ expense.requester }}</p>
              </td>
              <td class="px-4 py-4 align-top">
                <div class="flex flex-wrap gap-2">
                  @if (expense.attachmentUrl) {
                    <a
                      [href]="expense.attachmentUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex items-center gap-1 font-semibold whitespace-nowrap text-sky-700 no-underline hover:underline"
                      >เอกสารเบิก <lucide-icon name="ExternalLink" [size]="12" aria-hidden="true" /></a
                    >
                  }
                  @if (expense.receiptUrl) {
                    <a
                      [href]="expense.receiptUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex items-center gap-1 font-semibold whitespace-nowrap text-sky-700 no-underline hover:underline"
                      >สลิปโอนจ่าย <lucide-icon name="ExternalLink" [size]="12" aria-hidden="true" /></a
                    >
                  }
                  @if (!expense.attachmentUrl && !expense.receiptUrl) {
                    <span class="text-slate-400">—</span>
                  }
                </div>
              </td>
              <td class="px-4 py-4 text-right align-top">
                @if (expense.actualTransferAmount !== null) {
                  <p class="m-0 font-semibold whitespace-nowrap text-slate-800 tabular-nums">
                    {{ expense.actualTransferAmount | number: '1.2-2' }} บาท
                  </p>
                  <p class="mt-1 mb-0 text-xs whitespace-nowrap text-slate-500 tabular-nums">
                    ประมาณ {{ expense.amount | number: '1.2-2' }} บาท
                  </p>
                } @else {
                  <p class="m-0 text-xs text-slate-400">ยังไม่ระบุยอดจริง</p>
                  <p class="mt-1 mb-0 font-medium whitespace-nowrap text-slate-600 tabular-nums">
                    ประมาณ {{ expense.amount | number: '1.2-2' }} บาท
                  </p>
                }
              </td>
              <td class="px-4 py-4 text-center align-top">
                <app-status-badge [status]="expense.status" />
              </td>
              @if (canManageTourExpenses()) {
                <td class="px-4 py-4 text-right align-top">
                  <div class="flex items-center justify-end gap-1.5">
                  @if (expense.status === 'WAITING_CLEARING') {
                    <button
                      type="button"
                      class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-sky-200 bg-white text-sky-700 transition hover:bg-sky-50"
                      title="ส่งใบเสร็จ"
                      aria-label="ส่งใบเสร็จ"
                      (click)="openExpenseReceiptModal(expense)"
                    >
                      <lucide-icon name="Upload" [size]="15" aria-hidden="true" />
                    </button>
                  }
                  @if (expense.status === 'DRAFT') {
                    <button
                      type="button"
                      class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
                      title="แก้ไขใบขอเบิก"
                      aria-label="แก้ไขใบขอเบิก"
                      (click)="editExpense(expense)"
                    >
                      <lucide-icon name="Pencil" [size]="15" />
                    </button>
                  }
                  @if (canManagePrimaryTourData() && expense.status === 'REJECTED' && expense.source !== 'TOUR_ESTIMATE') {
                    <button
                      type="button"
                      class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-red-200 bg-white text-red-600 transition hover:bg-red-50"
                      title="ลบใบขอเบิก"
                      aria-label="ลบใบขอเบิก"
                      (click)="deleteExpense(expense)"
                    >
                      <lucide-icon name="Trash2" [size]="15" />
                    </button>
                  }
                  </div>
                </td>
              }
            </tr>
          } @empty {
            <tr>
              <td
                [attr.colspan]="canManageTourExpenses() ? 7 : 6"
                class="px-4 py-10 text-center text-slate-500"
              >
                ยังไม่มีรายการค่าใช้จ่ายทัวร์
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>

  <section class="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div
      class="flex flex-col gap-3 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
    >
      <div>
        <h2 class="m-0 text-lg font-bold text-slate-800">ปรับวงเงินและเงินสำรอง</h2>
        <p class="mt-1 mb-0 text-sm text-slate-500">
          คำขอที่เกี่ยวข้องกับทัวร์นี้ {{ advanceApprovals().length }} รายการ
        </p>
      </div>
      @if (canManageTourExpenses()) {
        <button
          type="button"
          class="tw-button inline-flex h-11 items-center justify-center gap-2 self-start rounded-xl bg-[var(--theme-color)] px-4 text-[15px] font-semibold text-white shadow-sm transition hover:bg-[var(--theme-hover)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:self-auto"
          (click)="openFinancialAdjustmentModal()"
        >
          <lucide-icon name="Plus" [size]="17" aria-hidden="true" />สร้างคำขอ
        </button>
      }
    </div>
    <div class="overflow-x-auto">
      <table class="w-full min-w-[900px] border-collapse text-left text-[13px]">
        <thead
          class="bg-slate-50 text-xs font-semibold tracking-wide whitespace-nowrap text-slate-500"
        >
          <tr>
            <th class="w-14 px-4 py-3 text-center">ลำดับ</th>
            <th class="w-40 px-4 py-3">คำขอ</th>
            <th class="min-w-72 px-4 py-3">รายละเอียด</th>
            <th class="w-64 px-4 py-3">รายการปรับ</th>
            <th class="w-28 px-4 py-3 text-center">เอกสาร</th>
            <th class="w-32 px-4 py-3 text-center">สถานะ</th>
            @if (canManageTourExpenses()) {
              <th class="w-28 px-4 py-3 text-center">ดำเนินการ</th>
            }
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100">
          @for (approval of advanceApprovals(); track approval.id; let index = $index) {
            <tr class="transition even:bg-slate-50/40 hover:bg-sky-50/60">
              <td class="px-4 py-4 text-center text-slate-400 tabular-nums align-top">
                {{ index + 1 }}
              </td>
              <td class="px-4 py-4 align-top">
                <span class="font-semibold whitespace-nowrap text-sky-700">
                  {{ approval.requestNo }}
                </span>
              </td>
              <td class="px-4 py-4 align-top">
                <p class="m-0 leading-5 font-medium text-slate-700">{{ approval.reason }}</p>
                <div
                  class="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500"
                >
                  <span>ผู้ขอ: {{ approval.requestedBy }}</span>
                  <span class="whitespace-nowrap">
                    {{ approval.requestedAt | date: 'dd MMM yyyy' }}
                  </span>
                </div>
              </td>
              <td class="px-4 py-4 align-top">
                <div class="grid gap-1.5">
                  @if (adjustmentAmount(approval.tourPriceDelta) > 0) {
                    <div class="flex items-center justify-between gap-4 rounded-lg bg-violet-50 px-2.5 py-1.5">
                      <span class="text-xs text-violet-700">เพิ่มค่าทัวร์ทั้งหมด</span>
                      <span class="font-semibold whitespace-nowrap text-violet-700 tabular-nums">+{{ approval.tourPriceDelta | number: '1.2-2' }} บาท</span>
                    </div>
                  }
                  @if (adjustmentAmount(approval.expenseBudgetDelta) > 0) {
                    <div class="flex items-center justify-between gap-4 rounded-lg bg-red-50 px-2.5 py-1.5">
                      <span class="text-xs text-red-700">วงเงินค่าใช้จ่าย</span>
                      <span class="font-semibold whitespace-nowrap text-red-700 tabular-nums">+{{ approval.expenseBudgetDelta | number: '1.2-2' }} บาท</span>
                    </div>
                  }
                  @if (adjustmentAmount(approval.customerChargeDelta) > 0) {
                    <div class="flex items-center justify-between gap-4 rounded-lg bg-emerald-50 px-2.5 py-1.5">
                      <span class="text-xs text-emerald-700">ยอดเรียกเก็บ</span>
                      <span class="font-semibold whitespace-nowrap text-emerald-700 tabular-nums">+{{ approval.customerChargeDelta | number: '1.2-2' }} บาท</span>
                    </div>
                  }
                  @if (adjustmentAmount(approval.companyAdvanceAmount) > 0) {
                    <div class="flex items-center justify-between gap-4 rounded-lg bg-sky-50 px-2.5 py-1.5">
                      <span class="text-xs text-sky-700">บริษัทสำรอง</span>
                      <span class="font-semibold whitespace-nowrap text-sky-700 tabular-nums">+{{ approval.companyAdvanceAmount | number: '1.2-2' }} บาท</span>
                    </div>
                  }
                </div>
              </td>
              <td class="px-4 py-4 text-center align-top">
                @if (approval.advanceAttachmentPath) {
                  <button
                    type="button"
                    class="tw-button inline-flex items-center gap-1 font-semibold whitespace-nowrap text-sky-700 hover:underline"
                    (click)="openAdjustmentAttachment(approval.advanceAttachmentPath)"
                  >
                    เปิดไฟล์ <lucide-icon name="ExternalLink" [size]="13" aria-hidden="true" />
                  </button>
                } @else {
                  <span class="text-slate-400">—</span>
                }
              </td>
              <td class="px-4 py-4 text-center align-top">
                <app-status-badge [status]="approval.status" />
              </td>
              @if (canManagePrimaryTourData()) {
                <td class="px-4 py-4 text-center align-top">
                  @if (approval.status === 'REJECTED') {
                    <button
                      type="button"
                      class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-red-200 bg-white text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                      title="ลบคำขอที่ไม่ผ่าน"
                      aria-label="ลบคำขอที่ไม่ผ่าน"
                      [disabled]="isDeletingFinancialAdjustment()"
                      (click)="deleteFinancialAdjustment(approval)"
                    >
                      <lucide-icon name="Trash2" [size]="15" aria-hidden="true" />
                    </button>
                  } @else {
                    <span class="text-slate-400">—</span>
                  }
                </td>
              }
            </tr>
          } @empty {
            <tr>
              <td
                [attr.colspan]="canManagePrimaryTourData() ? 7 : 6"
                class="px-4 py-10 text-center text-slate-500"
              >
                ยังไม่มีคำขอเงินสำรอง
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>

  <section class="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div
        class="flex flex-col gap-3 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
      >
        <div>
          <h2 class="m-0 text-lg font-bold text-slate-800">ค่าคอมมิชชั่น</h2>
        <p class="mt-1 mb-0 text-sm text-slate-500">รายการค่าคอมมิชชั่นที่เกี่ยวข้องกับทัวร์นี้</p>
        </div>
      </div>
    <div class="overflow-x-auto">
      <table class="w-full min-w-[900px] border-collapse text-left text-[13px]">
        <thead
          class="bg-slate-50 text-xs font-semibold tracking-wide whitespace-nowrap text-slate-500"
        >
          <tr>
            <th class="w-14 px-4 py-3 text-center">ลำดับ</th>
            <th class="w-44 px-4 py-3">รายการ</th>
            <th class="min-w-72 px-4 py-3">รายละเอียดค่าคอมมิชชั่น</th>
            <th class="w-44 px-4 py-3 text-right">ยอดรวม</th>
            <th class="w-40 px-4 py-3 text-right">ยอดได้รับจริง</th>
            <th class="w-44 px-4 py-3 text-right">ยอดหักคืน</th>
            <th class="w-28 px-4 py-3 text-center">เอกสาร</th>
            <th class="w-32 px-4 py-3 text-center">สถานะ</th>
            <th class="w-24 px-4 py-3 text-right">ดำเนินการ</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100">
          @for (commission of tourCommissionRequests(); track commission.id; let index = $index) {
            <tr class="transition even:bg-slate-50/40 hover:bg-sky-50/60">
              <td class="px-4 py-4 text-center text-slate-400 tabular-nums align-top">
                {{ index + 1 }}
              </td>
              <td class="px-4 py-4 align-top">
                <span class="font-semibold whitespace-nowrap text-sky-700">
                  {{ commission.commissionNo }}
                </span>
              </td>
              <td class="px-4 py-4 align-top">
                <p class="m-0 font-medium text-slate-800">{{ commission.administrator }}</p>
                <p class="mt-1 mb-0 text-xs text-slate-500">{{ commission.note }}</p>
              </td>
              <td
                class="px-4 py-4 text-right font-semibold whitespace-nowrap text-slate-800 align-top"
              >
                {{ commission.amount | number: '1.2-2' }} บาท
              </td>
              <td class="px-4 py-4 text-right align-top">
                @if (commission.status === 'VERIFIED') {
                  <p class="m-0 font-bold whitespace-nowrap text-emerald-700 tabular-nums">
                    {{ commission.paidAmount | number: '1.2-2' }} บาท
                  </p>
                } @else {
                  <span class="text-slate-400">—</span>
                }
              </td>
              <td class="px-4 py-4 text-right align-top">
                @if (commission.recoveries.length > 0) {
                  <p class="m-0 font-bold whitespace-nowrap text-red-600 tabular-nums">
                    -{{ tourCommissionRecoveryTotal(commission, 'appliedAmount') | number: '1.2-2' }} บาท
                  </p>
                  <p class="mt-1 mb-0 text-xs whitespace-nowrap text-slate-500">
                    คงเหลือ
                    <span class="font-semibold text-red-600">
                      {{ tourCommissionRecoveryTotal(commission, 'remainingAmount') | number: '1.2-2' }} บาท
                    </span>
                  </p>
                  <button
                    type="button"
                    class="tw-button mt-2 inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:text-sky-800 hover:underline"
                    (click)="openTourCommissionRecoveryHistory(commission)"
                  >
                    <lucide-icon name="History" [size]="13" aria-hidden="true" /> ดูประวัติ
                  </button>
                } @else {
                  <span class="text-slate-400">—</span>
                }
              </td>
              <td class="px-4 py-4 text-center align-top text-slate-400">—</td>
              <td class="px-4 py-4 text-center align-top">
                <app-status-badge [status]="commission.status" />
              </td>
              <td class="px-4 py-4 text-right align-top">
                @if (canSubmitTourCommission(commission)) {
                  <button
                    type="button"
                    class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white p-0 text-slate-600 hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700"
                    title="ส่งเบิกค่าคอมมิชชั่น"
                    (click)="openTourCommissionModal(commission)"
                  >
                    <lucide-icon name="Send" [size]="15" aria-hidden="true" />
                    <span class="sr-only">ส่งเบิกค่าคอมมิชชั่น</span>
                  </button>
                } @else {
                  <span class="text-slate-400">—</span>
                }
              </td>
            </tr>
          } @empty {
            <tr>
              <td colspan="9" class="px-4 py-10 text-center text-slate-500">
                ยังไม่มีรายการค่าคอมมิชชั่นผู้ดูแลทัวร์
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>
} @else {
  <section class="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
    <h2 class="m-0 text-lg font-bold text-slate-800">ไม่พบข้อมูลทัวร์</h2>
    <p class="mt-2 text-sm text-slate-500">รายการทัวร์นี้อาจถูกลบหรือไม่มีอยู่ในระบบ</p>
    <a
      routerLink="/tours"
      class="tw-button mt-4 inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-[15px] font-semibold text-slate-700 no-underline transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
      ><lucide-icon name="ArrowLeft" [size]="17" />กลับหน้ารายการ</a
    >
  </section>
}

@if (selectedTourCommissionRecovery(); as commission) {
  <div
    class="fixed inset-0 z-[230] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
    (click)="closeTourCommissionRecoveryHistory()"
    (keydown.escape)="closeTourCommissionRecoveryHistory()"
  >
    <section
      class="my-auto w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-commission-recovery-title"
      (click)="$event.stopPropagation()"
    >
      <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
        <div>
          <p class="mb-1 text-xs font-semibold tracking-wider text-red-600 uppercase">ประวัติยอดหักคืน</p>
          <h2 id="tour-commission-recovery-title" class="m-0 text-xl font-bold text-slate-800">
            {{ commission.commissionNo }} · {{ commission.administrator }}
          </h2>
          <p class="mt-1 mb-0 text-sm text-slate-500">ยอดหักคืนค่าคอมมิชชั่นผู้ดูแล Tour</p>
        </div>
        <button
          type="button"
          class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          aria-label="ปิดหน้าต่าง"
          (click)="closeTourCommissionRecoveryHistory()"
        ><lucide-icon name="X" [size]="19" aria-hidden="true" /></button>
      </div>

      <div class="grid gap-3 border-b border-slate-100 bg-slate-50 px-5 py-4 sm:grid-cols-3 sm:px-6">
        <div>
          <p class="m-0 text-xs font-medium text-slate-500">ยอดที่หักกับค่าคอมรายการนี้</p>
          <p class="mt-1 mb-0 text-lg font-bold text-red-600 tabular-nums">{{ tourCommissionRecoveryTotal(commission, 'appliedAmount') | number: '1.2-2' }} บาท</p>
        </div>
        <div>
          <p class="m-0 text-xs font-medium text-slate-500">หักคืนแล้ว</p>
          <p class="mt-1 mb-0 text-lg font-bold text-emerald-700 tabular-nums">{{ tourCommissionRecoveryTotal(commission, 'settledAmount') | number: '1.2-2' }} บาท</p>
        </div>
        <div>
          <p class="m-0 text-xs font-medium text-slate-500">ยอดคงเหลือ</p>
          <p class="mt-1 mb-0 text-lg font-bold text-red-600 tabular-nums">{{ tourCommissionRecoveryTotal(commission, 'remainingAmount') | number: '1.2-2' }} บาท</p>
        </div>
      </div>

      <div class="max-h-[60vh] space-y-3 overflow-y-auto px-5 py-5 sm:px-6">
        @for (recovery of commission.recoveries; track recovery.id) {
          <article class="rounded-xl border border-slate-200 bg-white p-4">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div class="flex flex-wrap items-center gap-2">
                  <p class="m-0 font-semibold text-slate-800">{{ recovery.bookingNo }} · {{ recovery.refundNo }}</p>
                  <span
                    class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1"
                    [class]="tourCommissionRecoveryStatusClass(recovery.status)"
                  >{{ tourCommissionRecoveryStatusLabel(recovery.status) }}</span>
                </div>
                <p class="mt-1 mb-0 text-xs text-slate-500">{{ recovery.createdAt | date: 'dd MMM yyyy, HH:mm' }}</p>
                <p class="mt-2 mb-0 text-sm text-slate-600">{{ recovery.reason }}</p>
              </div>
              <div class="grid grid-cols-3 gap-x-4 text-right tabular-nums">
                <div><p class="m-0 text-[11px] text-slate-500">หักรายการนี้</p><p class="mt-1 mb-0 font-semibold text-red-600">{{ recovery.appliedAmount | number: '1.2-2' }}</p></div>
                <div><p class="m-0 text-[11px] text-slate-500">หักแล้ว</p><p class="mt-1 mb-0 font-semibold text-emerald-700">{{ recovery.settledAmount | number: '1.2-2' }}</p></div>
                <div><p class="m-0 text-[11px] text-slate-500">คงเหลือ</p><p class="mt-1 mb-0 font-semibold text-red-600">{{ recovery.remainingAmount | number: '1.2-2' }}</p></div>
              </div>
            </div>
            <div class="mt-4 border-t border-slate-100 pt-3">
              <p class="m-0 text-xs font-semibold text-slate-600">ประวัติการนำยอดไปหัก</p>
              @for (application of recovery.applications; track application.id) {
                <div class="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs">
                  <span class="font-medium text-slate-700">{{ application.targetCode }}</span>
                  <span class="text-slate-500">{{ application.status === 'RESERVED' ? 'กันยอดไว้' : application.status === 'APPLIED' ? 'หักคืนแล้ว' : 'คืนยอดแล้ว' }}</span>
                  <span class="font-semibold text-slate-800 tabular-nums">{{ application.amount | number: '1.2-2' }} บาท</span>
                </div>
              } @empty {
                <p class="mt-2 mb-0 text-xs text-slate-500">ยังไม่มีการนำยอดนี้ไปหักกับคอมมิชชั่นรอบใหม่</p>
              }
            </div>
          </article>
        }
      </div>
      <div class="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-4 sm:px-6">
        <button type="button" class="tw-button h-10 rounded-xl border border-slate-300 bg-white px-4 font-semibold text-slate-700 hover:bg-slate-100" (click)="closeTourCommissionRecoveryHistory()">ปิด</button>
      </div>
    </section>
  </div>
}

@if (selectedTourCommission(); as commission) {
  <div
    class="fixed inset-0 z-[220] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
    (click)="closeTourCommissionModal()"
  >
    <section
      class="my-auto w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-commission-title"
      (click)="$event.stopPropagation()"
    >
      <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
        <div>
          <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
            คำขอเบิกค่าคอมมิชชั่น
          </p>
          <h2 id="tour-commission-title" class="m-0 text-xl font-bold text-slate-800">
            เบิกค่าคอมมิชชั่นผู้ดูแล
          </h2>
          <p class="mt-1 mb-0 text-sm text-slate-500">{{ tour()?.tourNo }}</p>
        </div>
        <button
          type="button"
          class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          aria-label="ปิดหน้าต่าง"
          [disabled]="isSubmittingTourCommission()"
          (click)="closeTourCommissionModal()"
        >
          <lucide-icon name="X" [size]="19" aria-hidden="true" />
        </button>
      </div>

      <div class="space-y-5 px-5 py-5 sm:px-6">
        <div class="grid gap-3 rounded-xl border border-sky-100 bg-sky-50/70 p-4 sm:grid-cols-3">
          <div>
            <p class="m-0 text-xs font-medium text-slate-500">ผู้รับสิทธิ์</p>
            <p class="mt-1 mb-0 font-semibold text-slate-800">{{ commission.administrator }}</p>
          </div>
          <div>
            <p class="m-0 text-xs font-medium text-slate-500">จำนวนผู้เดินทาง</p>
            <p class="mt-1 mb-0 font-semibold text-slate-800">
              {{ commission.pax | number: '1.0-0' }} คน
            </p>
          </div>
          <div>
            <p class="m-0 text-xs font-medium text-slate-500">ยอดเบิกทั้งหมด</p>
            <p class="mt-1 mb-0 text-lg font-bold text-sky-700">
              {{ commission.amount | number: '1.2-2' }} บาท
            </p>
          </div>
          <div>
            <p class="m-0 text-xs font-medium text-slate-500">ยอดหักคืนค้างอยู่</p>
            <p class="mt-1 mb-0 font-semibold text-red-600">
              {{ commission.recoveryOutstanding | number: '1.2-2' }} บาท
            </p>
          </div>
          <div>
            <p class="m-0 text-xs font-medium text-slate-500">ยอดที่จะหักครั้งนี้</p>
            <p class="mt-1 mb-0 font-semibold text-red-600">
              -{{ commission.recoveryDeduction | number: '1.2-2' }} บาท
            </p>
          </div>
          <div>
            <p class="m-0 text-xs font-medium text-slate-500">ยอดรับสุทธิ (โดยประมาณ)</p>
            <p class="mt-1 mb-0 text-lg font-bold text-emerald-700">
              {{ commission.netPaymentAmount | number: '1.2-2' }} บาท
            </p>
          </div>
          <div>
            <p class="m-0 text-xs font-medium text-slate-500">อัตราคอมมิชชั่นผู้ดูแล</p>
            <p class="mt-1 mb-0 font-semibold text-slate-800">
              {{ commission.commissionRate | number: '1.2-2' }}%
            </p>
          </div>
          <div class="sm:col-span-2">
            <p class="m-0 text-xs font-medium text-slate-500">เลขที่รายการ</p>
            <p class="mt-1 mb-0 font-semibold text-slate-800">{{ commission.commissionNo }}</p>
          </div>
        </div>
        <div class="rounded-xl border border-slate-200 bg-white px-4 py-3">
          <p class="m-0 text-xs font-medium text-slate-500">รายละเอียดการคำนวณ</p>
          <p class="mt-1 mb-0 text-sm font-semibold text-slate-700">{{ commission.note }}</p>
        </div>
        <p class="m-0 text-xs text-slate-500">
          ยอดหักและยอดรับสุทธิจะคำนวณใหม่อีกครั้งเมื่อฝ่ายบัญชีอนุมัติจ่ายเงิน
        </p>
      </div>

      <div
        class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6"
      >
        <button
          type="button"
          class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          [disabled]="isSubmittingTourCommission()"
          (click)="closeTourCommissionModal()"
        >
          ยกเลิก
        </button>
        <button
          type="button"
          class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-5 text-[15px] font-semibold text-white hover:bg-[var(--theme-hover)] disabled:opacity-50"
          [disabled]="isSubmittingTourCommission()"
          (click)="submitTourCommission()"
        >
          <lucide-icon name="Send" [size]="18" aria-hidden="true" />
          {{ isSubmittingTourCommission() ? 'กำลังส่ง...' : 'ส่งเบิกค่าคอม' }}
        </button>
      </div>
    </section>
  </div>
}

@if (isAddAdministratorOpen()) {
  <div
    class="fixed inset-0 z-[200] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
    (click)="closeAddAdministrator()"
  >
    <section
      class="my-auto w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-administrator-title"
      (click)="$event.stopPropagation()"
    >
      <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
        <div>
          <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
            ข้อมูลผู้ดูแลทัวร์
          </p>
          <h2 id="add-administrator-title" class="m-0 text-xl font-bold text-slate-800">
            เพิ่มผู้ดูแลทัวร์
          </h2>
        </div>
        <button
          type="button"
          class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          aria-label="ปิดหน้าต่าง"
          (click)="closeAddAdministrator()"
        >
          <lucide-icon name="X" [size]="19" aria-hidden="true" />
        </button>
      </div>

      <form [formGroup]="administratorForm" (ngSubmit)="submitAddAdministrator()">
        <div class="grid gap-4 px-5 py-6 sm:px-6">
          <label class="grid gap-1.5 text-sm font-semibold text-slate-700">
            <span>เลือกผู้ดูแล <span class="text-red-500">*</span></span>
            <p-select
              class="w-full font-normal"
              styleClass="h-11 w-full"
              formControlName="userId"
              [options]="administratorOptions()"
              optionLabel="label"
              optionValue="value"
              placeholder="เลือกผู้ดูแลทัวร์"
              appendTo="body"
              [filter]="true"
              filterBy="label"
              [loading]="isLoadingAdministratorOptions()"
              [emptyMessage]="
                isLoadingAdministratorOptions()
                  ? 'กำลังโหลดรายชื่อผู้ใช้...'
                  : 'ไม่มีรายชื่อผู้ใช้ที่สามารถเลือกได้'
              "
            />
            @if (
              administratorForm.controls.userId.touched && administratorForm.controls.userId.invalid
            ) {
              <small class="text-xs font-normal leading-5 text-red-600"
                >กรุณาเลือกผู้ดูแลทัวร์</small
              >
            }
          </label>

          <label class="grid gap-1.5 text-sm font-semibold text-slate-700">
            <span>หน้าที่</span>
            <input
              pInputText
              class="h-11 w-full font-normal"
              formControlName="responsibility"
              placeholder="เช่น หัวหน้าทัวร์ ผู้ช่วยผู้ดูแล"
            />
            @if (administratorForm.controls.responsibility.hasError('maxlength')) {
              <small class="text-xs font-normal leading-5 text-red-600"
                >หน้าที่ต้องไม่เกิน 100 ตัวอักษร</small
              >
            }
          </label>

          <label
            class="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-700"
            for="tour-administrator-primary"
          >
            <p-checkbox
              inputId="tour-administrator-primary"
              formControlName="isPrimary"
              [binary]="true"
            />
            <span
              ><span class="block">กำหนดเป็นผู้ดูแลหลัก</span
              ><small class="font-normal text-slate-500"
                >ผู้รับผิดชอบหลักของทัวร์นี้ (มีได้เพียงคนเดียว)</small
              ></span
            >
          </label>
        </div>

        <div
          class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6"
        >
          <button
            type="button"
            class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
            (click)="closeAddAdministrator()"
          >
            ยกเลิก
          </button>
          <button
            type="submit"
            class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-5 text-[15px] font-semibold text-white hover:bg-[var(--theme-hover)] disabled:cursor-not-allowed disabled:opacity-50"
            [disabled]="isSavingAdministrator() || isLoadingAdministratorOptions()"
          >
            <lucide-icon name="Check" [size]="18" aria-hidden="true" />
            {{ isSavingAdministrator() ? 'กำลังบันทึก...' : 'บันทึกผู้ดูแล' }}
          </button>
        </div>
      </form>
    </section>
  </div>
}

@if (isExpenseModalOpen()) {
  <div
    class="fixed inset-0 z-[200] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
    (click)="closeExpenseModal()"
  >
    <section
      class="my-auto w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-expense-title"
      (click)="$event.stopPropagation()"
    >
      <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
        <div>
          <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
            ข้อมูลค่าใช้จ่าย
          </p>
          <h2 id="tour-expense-title" class="m-0 text-xl font-bold text-slate-800">
            {{ editingExpenseId() ? 'แก้ไขใบขอเบิก' : 'สร้างใบขอเบิก' }}
          </h2>
        </div>
        <button
          type="button"
          class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          aria-label="ปิดหน้าต่าง"
          (click)="closeExpenseModal()"
        >
          <lucide-icon name="X" [size]="19" />
        </button>
      </div>
      <form [formGroup]="expenseForm" (ngSubmit)="submitExpense(false)">
        <div class="grid gap-x-6 gap-y-0 px-5 py-5 sm:grid-cols-2 sm:px-6">
          <label
            class="grid content-start gap-1 text-sm font-semibold text-slate-700 sm:col-span-2"
          >
            <span>ประเภทค่าใช้จ่าย <span class="text-red-500">*</span></span>
            <p-select
              class="h-11 w-full font-normal"
              formControlName="expenseNameSnapshot"
              [options]="expenseTypeOptions()"
              placeholder="เลือกประเภทค่าใช้จ่าย"
              appendTo="body"
              emptyMessage="ยังไม่มีประเภทค่าใช้จ่าย (รอเชื่อม API)"
              (onChange)="onExpenseTypeChange()"
            />
            <span class="min-h-4">
              @if (isExpenseFieldInvalid('expenseNameSnapshot')) {
                <small class="font-normal text-red-600">กรุณาเลือกประเภทค่าใช้จ่าย</small>
              }
            </span>
          </label>
          @if (editingExpenseSource() === 'TOUR_ESTIMATE') {
            <label class="grid content-start gap-1 text-sm font-semibold text-slate-700">
              <span>ยอดโดยประมาณ</span>
              <p-inputnumber
                class="w-full font-normal"
                inputStyleClass="h-11 w-full bg-slate-100 text-slate-600"
                formControlName="estimatedAmount"
                mode="currency"
                currency="THB"
                locale="th-TH"
                placeholder="0.00"
              />
              <span class="min-h-4"></span>
            </label>
          }
          <label
            class="grid content-start gap-1 text-sm font-semibold text-slate-700"
            [class.sm:col-span-2]="editingExpenseSource() !== 'TOUR_ESTIMATE'"
          >
            <span>ยอดเงินจริง <span class="text-red-500">*</span></span>
            <p-inputnumber
              class="w-full font-normal"
              inputStyleClass="h-11 w-full"
              formControlName="actualTransferAmount"
              mode="currency"
              currency="THB"
              locale="th-TH"
              [min]="1"
              [max]="expenseActualTransferLimit()"
              placeholder="0.00"
              (onInput)="checkExpenseAmount($event.value)"
            />
            <span class="min-h-4">
              @if (isExpenseFieldInvalid('actualTransferAmount')) {
                @if (expenseForm.controls.actualTransferAmount.hasError('max')) {
                  <small class="font-normal text-red-600">
                    ยอดเงินจริงต้องไม่เกิน {{ expenseActualTransferLimit() | number: '1.2-2' }} บาท
                  </small>
                } @else {
                  <small class="font-normal text-red-600">กรุณากรอกยอดเงินจริงมากกว่า 0</small>
                }
              }
            </span>
          </label>
          <label
            class="grid content-start gap-1 text-sm font-semibold text-slate-700 sm:col-span-2"
          >
            <span>รายละเอียด <span class="text-red-500">*</span></span>
            <textarea
              pTextarea
              class="min-h-24 w-full resize-y font-normal"
              formControlName="description"
              placeholder="ระบุรายละเอียดที่ต้องการขอเบิก"
              [autoResize]="true"
            ></textarea>
            <span class="min-h-4">
              @if (isExpenseFieldInvalid('description')) {
                <small class="font-normal text-red-600">กรุณากรอกรายละเอียด</small>
              }
            </span>
          </label>
          @if (!isMiscellaneousExpense()) {
            <div class="grid content-start gap-1 text-sm font-semibold text-slate-700 sm:col-span-2">
              <span>แนบใบสั่งซื้อ <span class="text-red-500">*</span></span>
              <div class="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
                <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <p-fileupload
                    #expenseUpload
                    mode="basic"
                    name="purchaseOrder"
                    chooseLabel="เลือกไฟล์"
                    accept=".pdf,.jpg,.jpeg,.png"
                    [maxFileSize]="5000000"
                    [auto]="false"
                    (onSelect)="selectPurchaseOrder($event)"
                    (onClear)="clearPurchaseOrder()"
                  /><button
                    type="button"
                    class="tw-button inline-flex h-10 items-center gap-2 rounded-lg border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 disabled:opacity-40"
                    [disabled]="!expenseUpload.files.length"
                    (click)="expenseUpload.clear(); clearPurchaseOrder()"
                  >
                    <lucide-icon name="X" [size]="16" /> ยกเลิกไฟล์
                  </button>
                </div>
                @if (purchaseOrderFileName()) {
                  <p class="mt-2 mb-0 text-xs font-normal text-sky-700">
                    {{ purchaseOrderFileName() }}
                  </p>
                }
                @if (existingPurchaseOrderUrl(); as attachmentUrl) {
                  <a
                    [href]="attachmentUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="mt-2 inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-sky-200 bg-white px-3 text-xs font-semibold text-sky-700 no-underline transition hover:bg-sky-50 hover:text-sky-900"
                    ><lucide-icon name="ExternalLink" [size]="15" aria-hidden="true" />ดูไฟล์เดิม</a
                  >
                }
                <p class="mt-2 mb-0 text-xs font-normal text-slate-500">
                  รองรับไฟล์ PDF, JPG และ PNG ขนาดไม่เกิน 5 MB
                </p>
              </div>
              @if (!purchaseOrderFileName() && expenseForm.touched) {
                <small class="font-normal text-red-600">กรุณาแนบไฟล์ใบสั่งซื้อ</small>
              }
            </div>
          }
        </div>
        <div
          class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6"
        >
          <button
            type="button"
            class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
            (click)="closeExpenseModal()"
          >
            ยกเลิก</button
          ><button
            type="submit"
            class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            <lucide-icon name="Check" [size]="18" />
            บันทึกร่าง
          </button>
          <button
            type="button"
            class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-5 text-[15px] font-semibold text-white hover:bg-[var(--theme-hover)] disabled:opacity-50"
            (click)="submitExpense(true)"
          >
            <lucide-icon name="Send" [size]="18" />
            ส่งเบิก
          </button>
        </div>
      </form>
    </section>
  </div>
}

@if (selectedReceiptExpense(); as expense) {
  <div
    class="fixed inset-0 z-[210] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
    (click)="closeExpenseReceiptModal()"
  >
    <section
      class="my-auto w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-expense-receipt-title"
      (click)="$event.stopPropagation()"
    >
      <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
        <div>
          <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
            {{ expense.expenseNo }}
          </p>
          <h2 id="tour-expense-receipt-title" class="m-0 text-xl font-bold text-slate-800">
            ส่งใบเสร็จ
          </h2>
        </div>
        <button
          type="button"
          class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          [disabled]="isSavingExpenseReceipt()"
          (click)="closeExpenseReceiptModal()"
        >
          <lucide-icon name="X" [size]="19" aria-hidden="true" />
        </button>
      </div>
      <div class="p-5 sm:p-6">
        <div class="grid gap-1 text-sm font-semibold text-slate-700">
          <span>แนบไฟล์ใบเสร็จ <span class="text-red-500">*</span></span>
          <div class="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
              <p-fileupload
                #tourReceiptUpload
                mode="basic"
                name="expenseReceipt"
                chooseLabel="เลือกไฟล์"
                accept=".pdf,.jpg,.jpeg,.png"
                [maxFileSize]="5000000"
                [auto]="false"
                (onSelect)="selectExpenseReceipt($event)"
                (onClear)="expenseReceiptFile.set(null); expenseReceiptFileName.set('')"
              />
              <button
                type="button"
                class="tw-button inline-flex h-10 items-center gap-2 rounded-lg border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 disabled:opacity-40"
                [disabled]="!tourReceiptUpload.files.length || isSavingExpenseReceipt()"
                (click)="tourReceiptUpload.clear(); expenseReceiptFile.set(null); expenseReceiptFileName.set('')"
              >
                <lucide-icon name="X" [size]="16" aria-hidden="true" /> ยกเลิกไฟล์
              </button>
            </div>
            @if (expenseReceiptFileName()) {
              <p class="mt-2 mb-0 break-all text-xs font-normal text-sky-700">
                {{ expenseReceiptFileName() }}
              </p>
            }
            <p class="mt-2 mb-0 text-xs font-normal text-slate-500">
              รองรับไฟล์ PDF, JPG และ PNG ขนาดไม่เกิน 5 MB
            </p>
          </div>
        </div>
      </div>
      <div class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
        <button
          type="button"
          class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
          [disabled]="isSavingExpenseReceipt()"
          (click)="closeExpenseReceiptModal()"
        >ยกเลิก</button>
        <button
          type="button"
          class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-5 text-[15px] font-semibold text-white hover:bg-[var(--theme-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          [disabled]="!expenseReceiptFile() || isSavingExpenseReceipt()"
          (click)="saveExpenseReceipt()"
        >
          <lucide-icon name="Check" [size]="18" aria-hidden="true" />
          {{ isSavingExpenseReceipt() ? 'กำลังบันทึก...' : 'บันทึกใบเสร็จ' }}
        </button>
      </div>
    </section>
  </div>
}

@if (isFinancialAdjustmentModalOpen()) {
  <div
    class="fixed inset-0 z-[200] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
    (click)="closeFinancialAdjustmentModal()"
  >
    <section
      class="my-auto w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-financial-adjustment-title"
      (click)="$event.stopPropagation()"
    >
      <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
        <div>
          <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
            จัดการวงเงินทัวร์
          </p>
          <h2 id="tour-financial-adjustment-title" class="m-0 text-xl font-bold text-slate-800">
            ปรับวงเงินและขอสำรองจ่าย
          </h2>
          <p class="mt-1 mb-0 text-sm text-slate-500">
            เลือกส่วนที่ต้องการปรับ แล้วตรวจสอบสรุปคำขอ
          </p>
        </div>
        <button
          type="button"
          class="tw-button inline-flex size-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          aria-label="ปิดหน้าต่าง"
          (click)="closeFinancialAdjustmentModal()"
        >
          <lucide-icon name="X" [size]="19" />
        </button>
      </div>

      <form [formGroup]="financialAdjustmentForm" (ngSubmit)="submitFinancialAdjustment()">
        <div class="grid gap-5 px-5 py-5 sm:px-6">
          <div
            class="rounded-xl border border-sky-100 bg-sky-50 p-4 text-sm leading-6 text-sky-800"
          >
            เลือกส่วนที่ต้องการปรับได้มากกว่าหนึ่งอย่าง แต่ละส่วนระบุยอดต่างกันได้
            <span class="mt-1 block text-xs text-sky-700"
              >คำขอจะถูกส่งเพื่อรอการอนุมัติ</span
            >
          </div>

          <div class="grid gap-3 sm:grid-cols-2">
            <div class="contents">
              <label
                class="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-4 text-sm font-semibold text-slate-800 transition hover:bg-sky-50"
              >
                <p-checkbox
                  formControlName="adjustTourPrice"
                  [binary]="true"
                  inputId="adjustTourPrice"
                />
                <span
                  >เพิ่มค่าทัวร์ทั้งหมด<small class="mt-1 block font-normal text-slate-500"
                    >ระบบเฉลี่ยยอดเพิ่มเป็นราคาต่อคนสำหรับ Booking ใหม่เท่านั้น</small
                  ></span
                >
              </label>
              <label
                class="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-4 text-sm font-semibold text-slate-800 transition hover:bg-sky-50"
              >
                <p-checkbox
                  formControlName="adjustExpenseBudget"
                  [binary]="true"
                  inputId="adjustExpenseBudget"
                />
                <span
                  >เพิ่มวงเงินค่าใช้จ่ายทัวร์<small class="mt-1 block font-normal text-slate-500"
                    >เพิ่มวงเงินสำหรับใช้เบิกค่าใช้จ่ายของทัวร์</small
                  ></span
                >
              </label>
            </div>

            <div class="contents">
              <label
                class="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-4 hover:bg-sky-50"
              >
                <p-checkbox
                  formControlName="adjustBookingCharges"
                  [binary]="true"
                  inputId="adjustBookingCharges"
                />
                <span class="text-sm font-semibold text-slate-800"
                  >เรียกเก็บลูกค้าเพิ่ม<small class="mt-1 block font-normal text-slate-500"
                    >เลือก Booking และยอดเพิ่ม</small
                  ></span
                >
              </label>
              <label
                class="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-4 hover:bg-sky-50"
              >
                <p-checkbox
                  formControlName="requestAdvance"
                  [binary]="true"
                  inputId="requestAdvance"
                />
                <span class="text-sm font-semibold text-slate-800"
                  >ขอสำรองจ่าย<small class="mt-1 block font-normal text-slate-500"
                    >ขอเงินบริษัทมาใช้ก่อน</small
                  ></span
                >
              </label>
            </div>
          </div>

          @if (financialAdjustmentForm.controls.adjustTourPrice.value) {
            <section
              class="rounded-xl border border-slate-200 bg-slate-50 p-4"
            >
              <label class="grid gap-2 text-sm font-semibold text-slate-700">
                <span>เพิ่มค่าทัวร์ทั้งหมด <span class="text-red-500">*</span></span>
                <p-inputnumber
                  formControlName="tourPriceDelta"
                  mode="currency"
                  currency="THB"
                  locale="th-TH"
                  [min]="0"
                  inputStyleClass="h-11 w-full"
                  styleClass="w-full"
                  placeholder="0.00"
                />
                <small class="font-normal text-sky-700"
                  >ยอดรวมเดิม {{ tour()?.tourTotalAmount ?? 0 | number: '1.2-2' }} → ยอดรวมใหม่
                  {{
                    (tour()?.tourTotalAmount ?? 0) +
                      (financialAdjustmentForm.controls.tourPriceDelta.value ?? 0) | number: '1.2-2'
                  }}
                  บาท</small
                >
              </label>
            </section>
          }

          @if (financialAdjustmentForm.controls.adjustExpenseBudget.value) {
            <section class="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <label class="grid gap-2 text-sm font-semibold text-slate-700">
                <span>เพิ่มวงเงินค่าใช้จ่ายทัวร์ <span class="text-red-500">*</span></span>
                <p-inputnumber
                  formControlName="expenseBudgetDelta"
                  mode="currency"
                  currency="THB"
                  locale="th-TH"
                  [min]="0"
                  inputStyleClass="h-11 w-full"
                  styleClass="w-full"
                  placeholder="0.00"
                />
                @if (expenseBudgetExceedsNetSales) {
                  <small role="alert" class="font-normal text-red-600"
                    >วงเงินค่าใช้จ่ายที่เพิ่มต้องไม่เกินยอดรวมสุทธิทั้งหมด
                    {{ bookingNetSalesTotal() | number: '1.2-2' }} บาท</small
                  >
                }
              </label>
            </section>
          }

          @if (financialAdjustmentForm.controls.adjustBookingCharges.value) {
            <section class="overflow-hidden rounded-xl border border-slate-200">
              <div class="border-b border-slate-200 bg-slate-50 p-4">
                <h3 class="m-0 text-sm font-bold text-slate-800">
                  เลือก Booking ที่ต้องการเรียกเก็บเพิ่ม
                </h3>
                <p class="mt-1 mb-0 text-xs text-slate-500">
                  กรอกยอดทั้งหมดที่ต้องการเพิ่มสำหรับแต่ละ Booking โดยไม่คูณจำนวนผู้เดินทาง
                </p>
              </div>
              <div formArrayName="bookingCharges" class="divide-y divide-slate-100">
                @for (
                  row of bookingChargeItems.controls;
                  track row.controls.bookingId.value;
                  let index = $index
                ) {
                  <div
                    [formGroupName]="index"
                    class="grid items-center gap-3 p-4 sm:grid-cols-[minmax(0,1fr)_220px]"
                  >
                    <label class="flex cursor-pointer items-center gap-3">
                      <p-checkbox
                        formControlName="selected"
                        [binary]="true"
                        [inputId]="'booking-' + row.controls.bookingId.value"
                      />
                      <span class="min-w-0 text-sm font-semibold text-slate-800"
                        >{{ row.controls.bookingNo.value
                        }}<small class="mt-1 block break-words font-normal text-slate-500">{{
                          row.controls.customerName.value
                        }} · {{ row.controls.pax.value }} คน</small></span
                      >
                    </label>
                    @if (row.controls.selected.value) {
                      <label class="grid gap-1 text-xs text-slate-600">
                        <span>ยอดทั้งหมดที่ต้องการเพิ่ม <span class="text-red-500">*</span></span>
                        <p-inputnumber
                          formControlName="amount"
                          mode="currency"
                          currency="THB"
                          locale="th-TH"
                          [min]="0"
                          inputStyleClass="h-11 w-full"
                          styleClass="w-full"
                          placeholder="0.00"
                        />
                      </label>
                    } @else {
                      <span class="text-xs text-slate-400 sm:text-right">ไม่ปรับยอด</span>
                    }
                  </div>
                } @empty {
                  <p class="m-0 p-5 text-center text-sm text-slate-500">
                    ยังไม่มี Booking ที่เลือกได้สำหรับทัวร์นี้
                  </p>
                }
              </div>
              <p
                class="m-0 border-t border-slate-200 bg-sky-50 p-4 text-right text-sm font-semibold text-sky-800"
              >
                เรียกเก็บเพิ่มรวม {{ adjustmentBookingTotal | number: '1.2-2' }} บาท
              </p>
            </section>
          }

          @if (financialAdjustmentForm.controls.requestAdvance.value) {
            <label
              class="grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-700"
            >
              <span>ยอดที่ขอให้บริษัทสำรองจ่าย <span class="text-red-500">*</span></span>
              <p-inputnumber
                formControlName="companyAdvanceAmount"
                mode="currency"
                currency="THB"
                locale="th-TH"
                [min]="0"
                inputStyleClass="h-11 w-full"
                styleClass="w-full"
                placeholder="0.00"
              />
              <small class="font-normal text-slate-500"
                >ระบุเงินที่ต้องใช้ก่อน ยอดนี้ไม่ใช่ยอดเรียกเก็บลูกค้า</small
              >
            </label>
          }

          @if (financialAdjustmentForm.controls.requestAdvance.value) {
            <div class="grid w-full content-start gap-1 text-sm font-semibold text-slate-700">
              <span>เอกสารประกอบการขอสำรองจ่าย</span>
              <div class="w-full rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
                <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <p-fileupload
                    #advanceUpload
                    mode="basic"
                    name="advanceAttachment"
                    chooseLabel="เลือกไฟล์"
                    accept=".pdf,.jpg,.jpeg,.png"
                    [maxFileSize]="5000000"
                    [auto]="false"
                    (onSelect)="selectAdvanceAttachment($event)"
                    (onClear)="advanceAttachmentFileName.set(''); advanceAttachmentFile.set(null)"
                  />
                  <button
                    type="button"
                    class="tw-button inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-3 text-sm font-semibold text-red-600 disabled:opacity-40"
                    [disabled]="!advanceUpload.files.length"
                    (click)="advanceUpload.clear(); advanceAttachmentFileName.set(''); advanceAttachmentFile.set(null)"
                  >
                    <lucide-icon name="X" [size]="16" /> ล้างไฟล์
                  </button>
                </div>
                @if (advanceAttachmentFileName()) {
                  <p class="mt-2 mb-0 break-all text-xs font-normal text-sky-700">
                    {{ advanceAttachmentFileName() }}
                  </p>
                }
                <p class="mt-2 mb-0 text-xs font-normal text-slate-500">
                  รองรับ PDF, JPG และ PNG ขนาดไม่เกิน 5 MB
                </p>
                @if (financialAdjustmentSubmitted() && !advanceAttachmentFile()) {
                  <small class="mt-2 block font-normal text-red-600"
                    >กรุณาแนบเอกสารประกอบการขอสำรองจ่าย</small
                  >
                }
              </div>
            </div>
          }

          <label class="grid content-start gap-1 text-sm font-semibold text-slate-700">
            <span>เหตุผลในการขอปรับยอด <span class="text-red-500">*</span></span>
            <textarea
              class="min-h-28 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2.5 font-normal outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              formControlName="reason"
              maxlength="500"
              placeholder="ระบุรายละเอียดและเหตุผลที่ต้องเพิ่มวงเงิน เรียกเก็บเพิ่ม หรือสำรองจ่าย"
            ></textarea>
            <span class="min-h-4">
              @if (
                financialAdjustmentForm.controls.reason.touched &&
                financialAdjustmentForm.controls.reason.invalid
              ) {
                <small class="font-normal text-red-600">กรุณาระบุเหตุผลในการขอปรับยอด</small>
              }
            </span>
          </label>
          <div
            class="grid gap-2 rounded-xl border border-sky-100 bg-sky-50 p-4 text-sm"
            aria-live="polite"
          >
            <strong class="text-slate-800">สรุปคำขอ</strong>
            <div class="flex justify-between gap-3">
              <span class="text-slate-600">ยอดค่าทัวร์ใหม่ทั้งหมด</span
              ><strong>{{ proposedTourTotalAmount | number: '1.2-2' }} บาท</strong>
            </div>
            <small class="text-slate-500">{{
              (financialAdjustmentForm.controls.tourPriceDelta.value ?? 0) > 0
                ? 'ปรับเพิ่มตามจำนวนเงินที่กรอก'
                : 'ไม่ปรับราคาทัวร์'
            }}</small>
            <div class="flex justify-between gap-3">
              <span class="text-slate-600">เพิ่มวงเงินค่าใช้จ่ายทัวร์</span
              ><strong>{{ adjustmentExpenseTotal | number: '1.2-2' }} บาท</strong>
            </div>
            <div class="flex justify-between gap-3">
              <span class="text-slate-600">เรียกเก็บ Booking เพิ่มรวม</span
              ><strong>{{ adjustmentBookingTotal | number: '1.2-2' }} บาท</strong>
            </div>
            <div class="flex justify-between gap-3">
              <span class="text-slate-600">ขอสำรองจ่าย</span
              ><strong>{{ adjustmentAdvanceTotal | number: '1.2-2' }} บาท</strong>
            </div>
            <p class="m-0 border-t border-sky-100 pt-2 text-xs text-slate-500">
              ยอดแต่ละส่วนมีวัตถุประสงค์ต่างกัน จึงไม่รวมเป็นยอดเดียว
            </p>
          </div>
          @if (financialAdjustmentSubmitted() && !isFinancialAdjustmentValid) {
            <p role="alert" class="m-0 text-sm text-red-600">
              ระบุยอดที่ต้องการเพิ่มอย่างน้อยหนึ่งรายการและกรอกเหตุผล หากเรียกเก็บ Booking
              เดิมให้เลือกอย่างน้อยหนึ่งรายการ
            </p>
          }
        </div>

        <div
          class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6"
        >
          <button
            type="button"
            class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
            (click)="closeFinancialAdjustmentModal()"
          >
            ยกเลิก
          </button>
          <button
            type="submit"
            class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-5 text-[15px] font-semibold text-white hover:bg-[var(--theme-hover)]"
            [disabled]="isSavingFinancialAdjustment()"
          >
            <lucide-icon name="Send" [size]="18" aria-hidden="true" />
            {{ isSavingFinancialAdjustment() ? 'กำลังบันทึก...' : 'บันทึกคำขอ' }}
          </button>
        </div>
      </form>
    </section>
  </div>
}

@if (editingBooking(); as booking) {
  <div
    class="fixed inset-0 z-[200] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
    (click)="closeDomesticEditModal()"
  >
    <section
      class="my-auto w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-domestic-booking-title"
      (click)="$event.stopPropagation()"
    >
      <div class="flex items-start justify-between border-b border-slate-200 p-5 sm:px-6">
        <div>
          <p class="mb-1 text-xs font-semibold text-sky-600">
            {{ booking.bookingType === 'GROUP_TOUR' ? 'การจองทัวร์เหมากรุ๊ป' : 'การจองทัวร์ภายใน' }} · {{ booking.bookingNo }}
          </p>
          <h2 id="edit-domestic-booking-title" class="m-0 text-xl font-bold text-slate-800">
            {{ booking.bookingType === 'GROUP_TOUR' ? 'แก้ไขข้อมูล Booking เหมากรุ๊ป' : 'แก้ไขข้อมูลการจองทัวร์ภายใน' }}
          </h2>
          <p class="mt-1 mb-0 text-sm text-slate-500">
            ปรับข้อมูลลูกค้า จำนวนผู้เดินทาง และส่วนลดของ Booking นี้
          </p>
        </div>
        <button
          type="button"
          class="tw-button inline-flex size-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
          aria-label="ปิดหน้าต่าง"
          (click)="closeDomesticEditModal()"
        >
          <lucide-icon name="X" [size]="19" />
        </button>
      </div>
      <form [formGroup]="domesticEditForm" (ngSubmit)="saveDomesticBookingChanges()">
        <div class="max-h-[68vh] space-y-5 overflow-y-auto p-5 sm:px-6">
          <div class="rounded-xl border border-sky-100 bg-sky-50 p-4">
            <p class="m-0 text-xs font-semibold text-sky-700">ทัวร์ที่ผูกกับการจอง</p>
            <p class="mt-1 mb-0 font-semibold text-slate-800">
              {{ booking.tourCode }} · {{ booking.tourName }}
            </p>
          </div>
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label
              class="grid content-start gap-1 text-sm font-semibold text-slate-700 sm:col-span-2"
            >
              <span>ชื่อลูกค้า <span class="text-red-500">*</span></span>
              <p-select
                class="w-full font-normal"
                formControlName="customerId"
                [options]="domesticCustomerOptions()"
                optionLabel="label"
                optionValue="value"
                [filter]="true"
                filterBy="label"
                placeholder="เลือกลูกค้า"
              />
              @if (
                domesticEditForm.controls.customerId.touched &&
                domesticEditForm.controls.customerId.invalid
              ) {
                <small class="font-normal text-red-600">กรุณาเลือกลูกค้า</small>
              }
            </label>
            <label class="grid content-start gap-1 text-sm font-semibold text-slate-700">
              <span>จำนวนคน <span class="text-red-500">*</span></span>
              <p-inputnumber
                class="w-full font-normal"
                inputStyleClass="h-11 w-full"
                formControlName="travelerCount"
                [min]="1"
                [maxFractionDigits]="0"
                [showButtons]="true"
              />
              @if (
                domesticEditForm.controls.travelerCount.touched &&
                domesticEditForm.controls.travelerCount.invalid
              ) {
                <small class="font-normal text-red-600">กรุณาระบุจำนวนเต็มอย่างน้อย 1 คน</small>
              }
            </label>
            <label class="grid content-start gap-1 text-sm font-semibold text-slate-700">
              <span>ส่วนลดทั้งหมด</span>
              <p-inputnumber
                class="w-full font-normal"
                inputStyleClass="h-11 w-full"
                formControlName="discount"
                mode="currency"
                currency="THB"
                locale="th-TH"
                [min]="0"
              />
              <small class="font-normal text-slate-500">ส่วนลดรวมของการจองนี้</small>
              @if (
                domesticEditForm.controls.discount.touched &&
                domesticEditForm.controls.discount.invalid
              ) {
                <small class="font-normal text-red-600">{{
                  domesticEditForm.controls.discount.hasError('discountExceeded')
                    ? 'ส่วนลดต้องไม่เกินยอดรวมการจอง'
                    : 'กรุณาระบุส่วนลดตั้งแต่ 0 บาท'
                }}</small>
              }
            </label>
          </div>
        </div>
        <div class="flex justify-end gap-3 border-t border-slate-200 px-5 py-4 sm:px-6">
          <button
            type="button"
            class="tw-button rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600"
            (click)="closeDomesticEditModal()"
          >
            ยกเลิก
          </button>
          <button
            type="submit"
            class="tw-button inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-5 py-2.5 font-semibold text-white shadow-sm transition hover:bg-[var(--theme-hover)] disabled:cursor-not-allowed disabled:opacity-50"
            [disabled]="isSavingDomesticEdit()"
          >
            <lucide-icon name="Check" [size]="18" aria-hidden="true" />{{
              isSavingDomesticEdit() ? 'กำลังบันทึก...' : 'บันทึกการแก้ไข'
            }}
          </button>
        </div>
      </form>
    </section>
  </div>
}
